import {
  Box,
  COLOR_PALETTE,
  Divider,
  Flex,
  Group,
  Icon,
  Text,
  Title,
  Tooltip,
} from '@inflearn/ds-react';
import { faCircleQuestion } from '@inflearn/pro-regular-svg-icons';
import useClientAccountProfileQuery from '../../../../queries/useClientAccountProfileQuery';

interface OrderSummaryData {
  totalCourseCount: number;
  totalOrderAmount: number;
  totalDiscountAmount: number;
  totalPaymentAmount: number;
}

interface OrderSummaryCardProps {
  data: OrderSummaryData;
}

export function OrderSummaryCard({ data }: OrderSummaryCardProps) {
  const { data: profile } = useClientAccountProfileQuery();
  const isAdmin = !!profile?.isAdmin;

  if (!isAdmin) {
    return null;
  }

  return (
    <Box
      bg="white"
      p={{ base: '12px 16px', sm: 16, md: 24 }}
      style={{
        border: `1px solid ${COLOR_PALETTE.gray[6]}`,
        borderRadius: 8,
      }}
    >
      <Flex
        direction={{ base: 'column', sm: 'row' }}
        align="center"
        justify="center"
        gap={{ base: 16, sm: 0 }}
      >
        {/* 총 구매 강의 수 */}
        <Flex
          w="100%"
          gap={8}
          style={{ flex: 1 }}
          justify="space-between"
          direction={{ base: 'row', sm: 'column' }}
        >
          <Group spacing={2} align="center">
            <Text fz={12} fw={500} lh={1.3} c="var(--inflearn-text-secondary)">
              총 구매 강의 수
            </Text>
            <Tooltip label="무료와 유료강의가 포함되어있어요." position="right" withArrow>
              <Icon icon={faCircleQuestion} size="xs" color="gray.5" />
            </Tooltip>
          </Group>
          <Group spacing={2}>
            <Title
              order={3}
              fz={{ base: 16, md: 22 }}
              fw={{ base: 700, sm: 700 }}
              c="var(--inflearn-text-primary)"
            >
              {data.totalCourseCount.toLocaleString()}
            </Title>
            <Title order={3} fz={{ base: 16, md: 22 }} fw={500} c="var(--inflearn-text-primary)">
              건
            </Title>
          </Group>
        </Flex>

        {/* 구분선 - 데스크톱에서만 표시 */}
        <Divider
          size="xs"
          w={1}
          h={23}
          orientation="vertical"
          display={{ base: 'none', sm: 'block' }}
          style={{ margin: '16px 24px' }}
        />

        {/* 총 주문 금액 */}
        <Flex
          w="100%"
          gap={8}
          style={{ flex: 1 }}
          justify="space-between"
          direction={{ base: 'row', sm: 'column' }}
        >
          <Text fz={12} fw={500} lh={1.3} c="var(--inflearn-text-secondary)">
            총 주문 금액
          </Text>
          <Group spacing={2}>
            <Title order={3} fz={{ base: 16, md: 22 }} fw={500} c="var(--inflearn-text-primary)">
              ￦
            </Title>
            <Title
              order={3}
              fz={{ base: 16, md: 22 }}
              fw={{ base: 700, sm: 700 }}
              c="var(--inflearn-text-primary)"
            >
              {data.totalOrderAmount.toLocaleString()}
            </Title>
          </Group>
        </Flex>

        {/* 구분선 - 데스크톱에서만 표시 */}
        <Divider
          orientation="vertical"
          size="xs"
          w={1}
          h={23}
          display={{ base: 'none', sm: 'block' }}
          style={{ margin: '16px 24px' }}
        />

        {/* 총 할인 금액 */}
        <Flex
          w="100%"
          gap={8}
          style={{ flex: 1 }}
          direction={{ base: 'row', sm: 'column' }}
          justify="space-between"
        >
          <Text fz={12} fw={500} lh={1.3} c="var(--inflearn-text-secondary)">
            총 할인 금액
          </Text>
          <Group spacing={2}>
            <Title order={3} fz={{ base: 16, md: 22 }} fw={500} c="var(--inflearn-text-primary)">
              ￦
            </Title>
            <Title
              order={3}
              fz={{ base: 16, md: 22 }}
              fw={{ base: 700, sm: 700 }}
              c="var(--inflearn-text-primary)"
            >
              {data.totalDiscountAmount.toLocaleString()}
            </Title>
          </Group>
        </Flex>

        {/* 구분선 - 데스크톱에서만 표시 */}
        <Divider
          w={1}
          h={23}
          orientation="vertical"
          size="xs"
          display={{ base: 'none', sm: 'block' }}
          style={{ margin: '16px 24px' }}
        />

        {/* 총 결제 금액 */}
        <Flex
          w="100%"
          gap={8}
          style={{ flex: 1 }}
          direction={{ base: 'row', sm: 'column' }}
          justify="space-between"
        >
          <Text fz={12} fw={500} lh={1.3} c="var(--inflearn-text-secondary)">
            총 결제 금액
          </Text>
          <Group spacing={2}>
            <Title order={3} fz={{ base: 16, md: 22 }} fw={500} c="var(--inflearn-text-primary)">
              ￦
            </Title>
            <Title
              order={3}
              fz={{ base: 16, md: 22 }}
              fw={{ base: 700, sm: 700 }}
              c="var(--inflearn-text-primary)"
            >
              {data.totalPaymentAmount.toLocaleString()}
            </Title>
          </Group>
        </Flex>
      </Flex>
    </Box>
  );
}
